/**
 * Centros de trabajo en el cliente.
 *
 * Se cachean en localStorage por dos razones:
 *  1. La geovalla se comprueba ANTES de pulsar. El trabajador ve "estás a
 *     840 m" en la propia pantalla de fichar, no un error del servidor
 *     después de haber pulsado.
 *  2. En una obra sin cobertura no hay a quién preguntar. Sin la caché, el
 *     fichaje offline no sabría a qué centro pertenece.
 *
 * La comprobación local es INFORMATIVA y nunca bloquea (ronda 4, r2b10[1]):
 * decide el backend, que tiene la regla tolerante con GPS malo (fuera de
 * radio con precisión dudosa → a revisión) y una geovalla fresca. Esta caché
 * puede estar rancia —el admin movió el pin y no hay red para refrescar— y
 * no conoce la precisión del fix: si bloqueara con estos datos, la jornada
 * real quedaría sin registrar y el único escape sería declararse REMOTE,
 * falsificando el ledger. Aquí sólo se AVISA de la distancia.
 */
import { getWorksites, Worksite } from './apiClient';

const CACHE_KEY = 'kakuat-worksites';

export function cachedWorksites(): Worksite[] {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as Worksite[]) : [];
  } catch {
    return [];
  }
}

export async function refreshWorksites(): Promise<Worksite[]> {
  const sites = await getWorksites();
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(sites));
  } catch {
    // Cuota llena: seguimos con lo que haya en memoria.
  }
  return sites;
}

export function findByUuid(uuid: string): Worksite | undefined {
  return cachedWorksites().find((w) => w.uuid === uuid);
}

/** Distancia en metros entre dos coordenadas (haversine). */
export function distanceMeters(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

export interface GeofenceCheck {
  inside: boolean;
  distance: number | null;   // null = no se puede saber (sin GPS o centro sin coordenadas)
  radius: number | null;
}

/**
 * ¿A cuántos metros del centro está esta posición? (`inside` = dentro de
 * radio). Informativo para la pantalla, JAMÁS un veredicto de bloqueo: la
 * caché puede estar rancia y aquí no entra la precisión del GPS.
 *
 * Sin GPS o sin geovalla configurada, `inside: true`: no bloqueamos por lo que
 * no podemos saber. Un fichaje nunca se pierde por falta de señal.
 */
export function checkGeofence(
  worksite: Worksite,
  position: { latitude?: number; longitude?: number } | null
): GeofenceCheck {
  const { geo_latitude, geo_longitude, geo_radius_meters, require_geofence } = worksite;

  if (!require_geofence || geo_latitude == null || geo_longitude == null || !geo_radius_meters) {
    return { inside: true, distance: null, radius: null };
  }
  if (position?.latitude == null || position?.longitude == null) {
    return { inside: true, distance: null, radius: geo_radius_meters };
  }

  const distance = distanceMeters(
    position.latitude,
    position.longitude,
    Number(geo_latitude),
    Number(geo_longitude)
  );

  return {
    inside: distance <= geo_radius_meters,
    distance: Math.round(distance),
    radius: geo_radius_meters,
  };
}
